// Morrow — distinct header for a thread that is a WORKFLOW RUN (not a normal chat).
// Shows a "Workflow" badge (ZapIcon, mirroring ChatHeader's save-as-workflow affordance),
// the workflow's name (resolved from the run's workflowId via the cached workflows list,
// falling back to the "⚡ "-stripped thread title), and a status pill.
import { Loader2Icon, ZapIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { fetchWorkflowSummaries } from "../../morrowAgents";
import { useI18n } from "~/i18n";
import { cn } from "~/lib/utils";

/** Strip the leading "⚡ " run marker so a thread title degrades to a readable name. */
function strippedTitle(title: string | null | undefined): string {
  return (title ?? "").replace(/^⚡\s*/u, "").trim();
}

function StatusPill({ status }: { status: string }) {
  const { t } = useI18n();
  const running = status === "running";
  const label =
    status === "running"
      ? t("workflowRun.statusRunning")
      : status === "ok"
        ? t("workflowRun.statusOk")
        : status === "failed"
          ? t("workflowRun.statusFailed")
          : status === "skipped"
            ? t("workflowRun.statusSkipped")
            : status;
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium capitalize",
        running && "bg-primary/10 text-primary",
        status === "ok" && "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
        status === "failed" && "bg-red-500/10 text-red-600 dark:text-red-400",
        (status === "skipped" || (!running && status !== "ok" && status !== "failed")) &&
          "bg-muted text-muted-foreground",
      )}
    >
      {running ? <Loader2Icon className="size-3 animate-spin" aria-hidden="true" /> : null}
      {label}
    </span>
  );
}

/**
 * The header shown in place of the normal <ChatHeader> for a workflow-run thread.
 * `workflowId` resolves the friendly workflow name; `fallbackTitle` is the thread title
 * used when no name is found; `status` drives the pill.
 */
export function WorkflowRunHeader({
  workflowId,
  fallbackTitle,
  status,
}: {
  workflowId: string | null;
  fallbackTitle: string | null | undefined;
  status: string;
}) {
  const { t } = useI18n();
  const [name, setName] = useState<string | null>(null);

  useEffect(() => {
    if (!workflowId) {
      setName(null);
      return;
    }
    let active = true;
    void fetchWorkflowSummaries().then((rows) => {
      if (!active) return;
      const match = rows.find((w) => w.id === workflowId);
      setName(match?.name?.trim() || null);
    });
    return () => {
      active = false;
    };
  }, [workflowId]);

  const displayName = name || strippedTitle(fallbackTitle) || t("workflowRun.badge");

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border/60 bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary">
        <ZapIcon className="size-3.5" />
        <span className="hidden sm:inline">{t("workflowRun.badge")}</span>
      </span>
      <h2
        aria-label={displayName}
        className="min-w-0 flex-1 truncate text-sm font-medium text-foreground"
      >
        {displayName}
      </h2>
      <StatusPill status={status} />
    </div>
  );
}
